import { z } from 'zod'
import { isLocalDay, validateJournalDay } from './journal'
import { localDayAt, parseIsoInstant } from './time'

export const MEALS = [{ value: 'breakfast', label: '早餐' }, { value: 'lunch', label: '午餐' }, { value: 'dinner', label: '晚餐' }, { value: 'snack', label: '加餐' }] as const
export type Meal = typeof MEALS[number]['value']
const instant = z.string().refine((v) => { try { return parseIsoInstant(v).toISOString() === v } catch { return false } })
export const nutrientSchema = z.strictObject({
  protein: z.number().finite().min(0).max(10000).optional(),
  carbs: z.number().finite().min(0).max(10000).optional(),
  fat: z.number().finite().min(0).max(10000).optional(),
  fiber: z.number().finite().min(0).max(10000).optional(),
  sodium: z.number().finite().min(0).max(1000000).optional(),
})
export type Nutrients = z.infer<typeof nutrientSchema>
export type NutrientDraft = { protein?: string; carbs?: string; fat?: string }
export function parseNutrientDraft(draft: NutrientDraft) {
  return nutrientSchema.parse(Object.fromEntries(Object.entries(draft).flatMap(([key, text]) => text.trim() ? [[key, Number(text)]] : [])))
}
export const NUTRIENTS = [
  { key: 'protein', label: '蛋白质', unit: 'g' }, { key: 'carbs', label: '碳水', unit: 'g' },
  { key: 'fat', label: '脂肪', unit: 'g' }, { key: 'fiber', label: '膳食纤维', unit: 'g' },
  { key: 'sodium', label: '钠', unit: 'mg' },
] as const
export const foodSchema = z.strictObject({
  id: z.string().min(1).max(100),
  name: z.string().trim().min(1).max(100),
  description: z.string().max(300).optional(),
  aliases: z.array(z.string().max(80)).max(20).optional(),
  origin: z.enum(['usda', 'tfda', 'boohee', 'custom', 'openFoodFacts', 'recipe', 'brand']),
  sourceId: z.string().max(50).optional(),
  sourceVersion: z.string().max(30).optional(),
  category: z.string().max(80).optional(),
  nutrients: nutrientSchema.optional(),
  portions: z.array(z.strictObject({ name: z.string().min(1).max(30), grams: z.number().finite().positive().max(10000) })).max(20).optional(),
  servingGrams: z.number().finite().positive().max(10000).optional(),
  drinkServing: z.strictObject({
    ml: z.number().finite().positive().max(10000),
    label: z.string().min(1).max(80),
    volumeBasis: z.enum(['cupCapacity', 'liquidVolume']),
    volumeSource: z.enum(['brand', 'reference']),
  }).optional(),
  brandSourceUrl: z.url().refine(v => new URL(v).protocol === 'https:', '来源需使用HTTPS').optional(),
  servingReference: z.strictObject({
    grams: z.number().finite().positive().max(10000),
    label: z.string().min(1).max(80),
    source: z.enum(['usda', 'bda', 'estimate']),
    referenceId: z.string().max(30).optional(),
  }).optional(),
  ingredients: z.array(z.string().min(1).max(80)).max(30).optional(),
  kcal: z.number().finite().min(0).max(10000),
  basis: z.union([z.literal(100), z.literal(1)]),
  unit: z.enum(['g', 'ml', '份']),
}).superRefine((v, ctx) => {
  if (v.servingGrams !== undefined && v.unit !== '份') ctx.addIssue({ code: 'custom', message: '每份重量仅适用于份单位' })
  if (v.drinkServing && (v.unit !== '份' || v.servingGrams !== undefined || v.servingReference !== undefined)) ctx.addIssue({ code: 'custom', message: '饮品杯量仅适用于独立的份单位，不能同时使用克数' })
  if (v.brandSourceUrl && v.origin !== 'brand') ctx.addIssue({ code: 'custom', message: '品牌来源仅适用于品牌食物' })
  if (v.origin === 'brand' && (!v.drinkServing || !v.brandSourceUrl)) ctx.addIssue({ code: 'custom', message: '品牌饮品需保留杯量与来源' })
  if (v.servingReference && (v.unit === 'ml' || (v.unit === '份' && v.servingReference.grams !== v.servingGrams))) ctx.addIssue({ code: 'custom', message: '参考重量与食物单位不一致' })
  if ((v.unit === '份') !== (v.basis === 1)) ctx.addIssue({ code: 'custom', message: '份量单位不匹配' })
  if (v.origin === 'recipe' && (!/^[a-z-]+-v\d+$/.test(v.sourceId ?? '') || v.unit !== '份')) ctx.addIssue({ code: 'custom', message: '配方来源无效' })
  if (v.origin === 'usda' && (!/^\d+$/.test(v.sourceId ?? '') || v.unit !== 'g')) ctx.addIssue({ code: 'custom', message: '食物来源无效' })
  if (v.origin === 'tfda' && (!/^[A-Z]\d+$/.test(v.sourceId ?? '') || v.unit !== 'g')) ctx.addIssue({ code: 'custom', message: '食物来源无效' })
  if (v.origin === 'boohee' && (!/^[A-Za-z0-9_-]{1,50}$/.test(v.sourceId ?? '') || v.unit !== 'g')) ctx.addIssue({ code: 'custom', message: '薄荷来源无效' })
  if (v.origin === 'openFoodFacts' && !/^\d{8,14}$/.test(v.sourceId ?? '')) ctx.addIssue({ code: 'custom', message: '条码无效' })
})
export type Food = z.infer<typeof foodSchema>
export function intakeKcal(food: Pick<Food, 'kcal' | 'basis'>, amount: number) {
  return Math.round(food.kcal * amount / food.basis * 10) / 10
}
export function intakeNutrients(food: Pick<Food, 'nutrients' | 'basis'>, amount: number): Nutrients {
  return Object.fromEntries(NUTRIENTS.flatMap(({ key }) => food.nutrients?.[key] === undefined ? [] : [[key, Math.round(food.nutrients[key] * amount / food.basis * 10) / 10]]))
}
// Totals must not represent an incomplete set of nutrient measurements as a complete meal/day.
export function totalNutrients(items: { food: Food; amount: number }[]): Nutrients {
  return Object.fromEntries(NUTRIENTS.flatMap(({ key }) => !items.length || items.some(({ food }) => food.nutrients?.[key] === undefined) ? [] :
    [[key, Math.round(items.reduce((sum, { food, amount }) => sum + food.nutrients![key]! * amount / food.basis, 0) * 10) / 10]]))
}
export const intakeSchema = z.strictObject({
  id: z.string().uuid(), localDay: z.string().refine(isLocalDay),
  timeZone: z.string().refine((v) => { try { localDayAt('2026-01-01T00:00:00Z', v); return v.length > 0 } catch { return false } }),
  meal: z.enum(['breakfast', 'lunch', 'dinner', 'snack']), food: foodSchema,
  amount: z.number().finite().positive().max(10000), kcal: z.number().finite().min(0).max(100000),
  source: z.literal('manual'), createdAt: instant, updatedAt: instant,
}).refine((v) => v.kcal === intakeKcal(v.food, v.amount), '热量与份量不一致')
export type IntakeRecord = z.infer<typeof intakeSchema>
export const savedFoodSchema = z.strictObject({ id: z.string().min(1).max(100), food: foodSchema, createdAt: instant,
  libraryOnly: z.literal(true).optional(), searchTerms: z.array(z.string().trim().min(1).max(30)).max(20).optional(),
})
  .refine((v) => v.id === v.food.id, '食物编号不匹配')
  .refine((v) => !v.libraryOnly || v.food.origin === 'boohee', '仅薄荷食物自动缓存')
export type SavedFood = z.infer<typeof savedFoodSchema>
export type IntakeInput = Pick<IntakeRecord, 'localDay' | 'timeZone' | 'meal' | 'food' | 'amount'>
export function createIntake(input: IntakeInput, now = new Date(), existing?: IntakeRecord): IntakeRecord {
  validateJournalDay(input.localDay, input.timeZone, now)
  return intakeSchema.parse({ ...input, food: foodSchema.parse(input.food), id: existing?.id ?? crypto.randomUUID(),
    kcal: intakeKcal(input.food, input.amount), source: 'manual', createdAt: existing?.createdAt ?? now.toISOString(), updatedAt: now.toISOString() })
}
export function validateNutritionSnapshot(snapshot: { intakes?: IntakeRecord[]; savedFoods?: SavedFood[] }) {
  for (const [records, schema] of [[snapshot.intakes === undefined ? [] : snapshot.intakes, intakeSchema], [snapshot.savedFoods === undefined ? [] : snapshot.savedFoods, savedFoodSchema]] as const) {
    if (!Array.isArray(records)) throw new Error('饮食记录格式无效')
    const ids = new Set<string>()
    for (const record of records) {
      schema.parse(record)
      if (ids.has(record.id)) throw new Error('饮食记录编号重复')
      ids.add(record.id)
    }
  }
}
export function foodSourceUrl(food: Food) {
  if (food.origin === 'brand') return food.brandSourceUrl
  if (food.origin === 'usda') return `https://fdc.nal.usda.gov/food-details/${food.sourceId}/nutrients`
  if (food.origin === 'boohee') return 'https://ai.boohee.com/'
  if (food.origin === 'tfda') return 'https://data.gov.tw/dataset/8543'
  if (food.origin === 'openFoodFacts') return `https://world.openfoodfacts.org/product/${food.sourceId}`
  return undefined
}
export function foodSourceLabel(food: Food) {
  return ({ brand: '品牌公开热量 · 查看对应规格', boohee: '薄荷健康 · 食物营养数据', usda: 'USDA FoodData Central · CC0', tfda: '食药署 · 政府资料开放授权第1版', openFoodFacts: 'Open Food Facts · ODbL', recipe: 'CareBaby参考配方 · USDA营养数据', custom: '自定义 · 按填写的标签计算' })[food.origin]
}
export function foodServingLabel(food: Food) {
  return food.drinkServing ? `1杯 ≈ ${formatKcal(food.drinkServing.ml)}ml` : `${food.basis}${food.unit}${food.servingGrams ? ` ≈ ${food.servingGrams}g` : ''}`
}
export function intakeAmountLabel(food: Food, amount: number) {
  return `${formatKcal(amount)}${food.drinkServing ? '杯' : food.unit}${food.drinkServing ? ` ≈ ${formatKcal(amount * food.drinkServing.ml)}ml` : food.servingGrams ? ` ≈ ${formatKcal(amount * food.servingGrams)}g` : ''}`
}
export function formatKcal(value: number) { return Number.isInteger(value) ? String(value) : value.toFixed(1) }
